import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { GridLayout } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import FleetMap from "../components/FleetMap";
import {
  Truck, WifiHigh, WifiSlash, Warning, ArrowsClockwise, Broadcast, CheckCircle,
  PencilSimple, X, Plus,
} from "@phosphor-icons/react";

const WIDGETS = {
  "kpi-total": { title: "Total Devices", kind: "kpi" },
  "kpi-online": { title: "Online", kind: "kpi" },
  "kpi-offline": { title: "Offline", kind: "kpi" },
  "kpi-emergency": { title: "Emergencies", kind: "kpi" },
  "kpi-expiring": { title: "Expiring", kind: "kpi" },
  "kpi-critical": { title: "Critical Alerts", kind: "kpi" },
  "map": { title: "Live Fleet Map", kind: "block" },
  "status": { title: "Vehicle Status", kind: "block" },
  "health": { title: "Device Health", kind: "block" },
  "gov": { title: "Government Data Push", kind: "block" },
  "system": { title: "System Health", kind: "block" },
};

const DEFAULT_LAYOUT = [
  { i: "kpi-total", x: 0, y: 0, w: 2, h: 1 }, { i: "kpi-online", x: 2, y: 0, w: 2, h: 1 },
  { i: "kpi-offline", x: 4, y: 0, w: 2, h: 1 }, { i: "kpi-emergency", x: 6, y: 0, w: 2, h: 1 },
  { i: "kpi-expiring", x: 8, y: 0, w: 2, h: 1 }, { i: "kpi-critical", x: 10, y: 0, w: 2, h: 1 },
  { i: "map", x: 0, y: 1, w: 8, h: 6 }, { i: "status", x: 8, y: 1, w: 4, h: 6 },
  { i: "health", x: 0, y: 7, w: 4, h: 4 }, { i: "gov", x: 4, y: 7, w: 4, h: 4 }, { i: "system", x: 8, y: 7, w: 4, h: 4 },
];

function Kpi({ label, value, icon: Icon, accent, onClick }) {
  return (
    <button onClick={onClick} data-testid="kpi-box" className="h-full w-full text-left p-3 flex flex-col justify-between relative overflow-hidden group" style={{ background: accent }}>
      <Icon size={70} weight="fill" className="absolute -right-3 -bottom-3 text-white opacity-20 group-hover:opacity-30 transition-opacity" />
      <span className="text-[10px] font-bold uppercase tracking-widest text-white/90 relative z-10">{label}</span>
      <div className="font-display font-black text-3xl tracking-tight text-white relative z-10">{value}</div>
    </button>
  );
}

function HealthBar({ label, pct }) {
  const color = pct > 99 ? "#34C759" : pct > 97 ? "#5AC8FA" : "#FF9500";
  return (
    <div>
      <div className="flex justify-between text-xs mb-1"><span className="text-muted-foreground">{label}</span><span className="font-mono font-semibold" style={{ color }}>{pct}%</span></div>
      <div className="h-1.5 bg-secondary rounded-sm overflow-hidden"><div className="h-full rounded-sm" style={{ width: `${pct}%`, background: color }} /></div>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [editing, setEditing] = useState(false);
  const [layout, setLayout] = useState(DEFAULT_LAYOUT);
  const [addOpen, setAddOpen] = useState(false);
  const nav = useNavigate();
  const { user } = useAuth();
  const LKEY = `vgps_dash_${user?.id || "anon"}`;
  const gridRef = useRef(null);
  const [width, setWidth] = useState(1100);
  useEffect(() => {
    if (!gridRef.current) return;
    const ro = new ResizeObserver((e) => setWidth(e[0].contentRect.width));
    ro.observe(gridRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    try { const s = localStorage.getItem(LKEY); if (s) setLayout(JSON.parse(s)); } catch { /* ignore */ }
  }, [LKEY]);

  useEffect(() => {
    const load = () => {
      api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
      api.get("/vehicles/live").then((r) => setVehicles(r.data.vehicles)).catch(() => {});
    };
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, []);

  const present = useMemo(() => new Set(layout.map((l) => l.i)), [layout]);
  const missing = Object.keys(WIDGETS).filter((id) => !present.has(id));

  const persist = (l) => { setLayout(l); try { localStorage.setItem(LKEY, JSON.stringify(l)); } catch { /* ignore */ } };
  const removeWidget = (id) => persist(layout.filter((l) => l.i !== id));
  const addWidget = (id) => { const w = WIDGETS[id].kind === "kpi" ? 2 : 4; const h = WIDGETS[id].kind === "kpi" ? 1 : 4; persist([...layout, { i: id, x: 0, y: 0, w, h }]); setAddOpen(false); };
  const reset = () => persist(DEFAULT_LAYOUT);

  if (!data) return <div className="p-6 text-muted-foreground">Loading command center…</div>;
  const k = data.kpis;

  if (k.total === 0) {
    return (
      <div className="p-5">
        <h1 className="font-display font-black text-2xl lg:text-3xl tracking-tight mb-1">AIS-140 Command Center</h1>
        <p className="text-sm text-muted-foreground">Real-time fleet operations overview</p>
        <div className="bg-card border border-dashed border-border rounded-sm p-10 flex flex-col items-center text-center max-w-2xl mx-auto mt-10" data-testid="onboarding-banner">
          <div className="w-16 h-16 rounded-full bg-primary/15 flex items-center justify-center mb-4"><Truck size={30} weight="bold" className="text-primary" /></div>
          <h2 className="font-display font-black text-xl mb-1">No devices yet</h2>
          <p className="text-sm text-muted-foreground max-w-md mb-5">Your fleet is empty. Register your first GPS device and map it to a vehicle to start live tracking, alerts and reports.</p>
          <div className="flex gap-2">
            <button data-testid="onboard-add-device" onClick={() => nav("/add-device")} className="flex items-center gap-2 bg-primary text-white font-semibold text-sm px-5 py-2.5 rounded-sm hover:opacity-90"><Truck size={16} weight="bold" /> Add your first device</button>
            <button data-testid="onboard-raw-data" onClick={() => nav("/raw-data")} className="flex items-center gap-2 border border-border font-semibold text-sm px-5 py-2.5 rounded-sm hover:border-primary"><Broadcast size={16} weight="bold" /> View Raw Data Feed</button>
          </div>
        </div>
      </div>
    );
  }

  const render = (id) => {
    switch (id) {
      case "kpi-total": return <Kpi label="Total Devices" value={k.total.toLocaleString()} icon={Truck} accent="#0091e6" onClick={() => nav("/vehicles")} />;
      case "kpi-online": return <Kpi label="Online" value={k.online.toLocaleString()} icon={WifiHigh} accent="#34C759" onClick={() => nav("/tracking")} />;
      case "kpi-offline": return <Kpi label="Offline" value={k.offline.toLocaleString()} icon={WifiSlash} accent="#8E8E93" onClick={() => nav("/device-health")} />;
      case "kpi-emergency": return <Kpi label="Emergencies" value={k.active_emergencies} icon={Warning} accent="#FF3B30" onClick={() => nav("/emergency")} />;
      case "kpi-expiring": return <Kpi label="Expiring" value={k.expiring} icon={ArrowsClockwise} accent="#FF9500" onClick={() => nav("/renewals")} />;
      case "kpi-critical": return <Kpi label="Critical Alerts" value={k.critical_alerts} icon={Warning} accent="#FF9500" onClick={() => nav("/alerts")} />;
      case "map": return <div className="h-full" data-testid="fleet-map"><FleetMap vehicles={vehicles} onSelect={() => nav("/tracking")} /></div>;
      case "status": return (
        <div className="p-3">
          {[["Moving", k.moving, "#34C759"], ["Idle", k.idle, "#5AC8FA"], ["Stopped", k.stopped, "#FF9500"], ["Offline", k.offline, "#8E8E93"], ["Emergency", k.panic, "#FF3B30"]].map(([label, val, color]) => (
            <div key={label} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
              <span className="flex items-center gap-2 text-sm"><span className="status-dot" style={{ background: color }} /> {label}</span>
              <span className="font-display font-bold text-lg" style={{ color }}>{val}</span>
            </div>
          ))}
        </div>
      );
      case "health": return (
        <div className="p-3 space-y-3">
          <HealthBar label="GPS" pct={data.health.gps} /><HealthBar label="GSM" pct={data.health.gsm} />
          <HealthBar label="Main Power" pct={data.health.power} /><HealthBar label="Panic Button" pct={data.health.panic_btn} />
          <div className="pt-2 border-t border-border text-sm flex justify-between"><span className="text-muted-foreground">Avg Health Score</span><span className="font-display font-bold text-primary">{data.health.avg_score}/100</span></div>
        </div>
      );
      case "gov": return (
        <div className="p-3 space-y-2">
          {data.gov_push.map((g) => (
            <div key={g.id} className="flex items-center justify-between text-sm">
              <span>{g.state}</span>
              <span className="flex items-center gap-2"><span className="font-mono" style={{ color: g.success_pct > 99 ? "#34C759" : "#FF9500" }}>{g.success_pct}%</span>
                {g.success_pct > 99 ? <CheckCircle size={14} weight="fill" color="#34C759" /> : <Warning size={14} weight="fill" color="#FF9500" />}</span>
            </div>
          ))}
        </div>
      );
      case "system": return (
        <div className="p-3 space-y-2.5">
          {["TCP Gateway", "Database", "Message Bus", "Gov Push"].map((label) => (
            <div key={label} className="flex items-center justify-between text-sm"><span>{label}</span>
              <span className="flex items-center gap-1.5 text-[#34C759] font-semibold text-xs"><CheckCircle size={15} weight="fill" /> Operational</span></div>
          ))}
        </div>
      );
      default: return null;
    }
  };

  return (
    <div className="p-5">
      <div className="flex items-end justify-between mb-4">
        <div>
          <h1 className="font-display font-black text-2xl lg:text-3xl tracking-tight">AIS-140 Command Center</h1>
          <p className="text-sm text-muted-foreground">Real-time fleet operations overview{editing && " · drag headers to move, drag corners to resize"}</p>
        </div>
        <div className="flex items-center gap-2 relative">
          {editing && (
            <>
              <button data-testid="dash-add-widget" onClick={() => setAddOpen((o) => !o)} disabled={!missing.length}
                className="flex items-center gap-1.5 border border-border text-sm font-semibold px-3 py-2 rounded-sm hover:border-primary disabled:opacity-40"><Plus size={15} weight="bold" /> Add Widget</button>
              <button data-testid="dash-reset" onClick={reset} className="flex items-center gap-1.5 border border-border text-sm font-semibold px-3 py-2 rounded-sm hover:border-primary"><ArrowsClockwise size={15} weight="bold" /> Reset</button>
              {addOpen && (
                <div className="absolute top-11 right-24 z-[2000] bg-popover border border-border rounded-sm p-1 w-52 shadow-xl" data-testid="add-widget-menu">
                  {missing.map((id) => <button key={id} onClick={() => addWidget(id)} className="block w-full text-left text-sm px-2 py-1.5 rounded-sm hover:bg-white/5">{WIDGETS[id].title}</button>)}
                </div>
              )}
            </>
          )}
          <button data-testid="dash-edit-toggle" onClick={() => { setEditing((e) => !e); setAddOpen(false); }}
            className={`flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-sm ${editing ? "bg-primary text-white" : "border border-border hover:border-primary"}`}>
            <PencilSimple size={15} weight="bold" /> {editing ? "Done" : "Customize"}
          </button>
        </div>
      </div>

      <div ref={gridRef}>
      <GridLayout className="layout" layout={layout} cols={12} rowHeight={72} margin={[14, 14]} width={width}
        isDraggable={editing} isResizable={editing} draggableHandle=".drag-handle"
        onLayoutChange={(l) => { if (editing) persist(l); }} compactType="vertical">
        {layout.map((l) => (
          <div key={l.i} data-testid={`widget-${l.i}`} className={`rounded-sm overflow-hidden flex flex-col ${WIDGETS[l.i]?.kind === "kpi" ? "shadow-sm" : `bg-card border ${editing ? "border-primary/50" : "border-border"}`}`}>
            {editing && (
              <div className="drag-handle flex items-center justify-between px-2 py-1 bg-secondary cursor-move shrink-0">
                <span className="label-caps text-[9px]">{WIDGETS[l.i]?.title}</span>
                <button onClick={() => removeWidget(l.i)} className="text-muted-foreground hover:text-destructive"><X size={13} weight="bold" /></button>
              </div>
            )}
            <div className="flex-1 min-h-0 overflow-hidden">{render(l.i)}</div>
          </div>
        ))}
      </GridLayout>
      </div>
    </div>
  );
}
